import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cornerstone from 'cornerstone-core';
import dicomParser from 'dicom-parser';
import cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import { Clock, Home, Link2Off, Share2 } from 'lucide-react';
import { api } from '../services/api';
import { downloadStudyArchive } from '../services/studyArchive';
import ImageCardList from './ImageCardList';
import DicomViewer from './DicomViewer';
import ImagingEmptyState from './ImagingEmptyState';
import ImagingLoadingState from './ImagingLoadingState';
import StudyListControls, { StudyListNoResults } from './StudyListControls';
import { useStudyListControls } from './useStudyListControls';
import { useSeriesHydration, withSeriesLabels } from './useSeriesHydration';
import { sortStudiesByDateDesc } from '../services/imagingTags';
import { describeShareExpiry } from '../services/imagingShares';
import './ImagingHeader.css';
import './SharedImagesPage.css';

cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

function SharedImagesPage({ toast }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const [share, setShare] = useState(null);
  const [studies, setStudies] = useState([]);
  const [status, setStatus] = useState('loading');

  const [currentStudyUID, setCurrentStudyUID] = useState('');
  const [currentSeriesKey, setCurrentSeriesKey] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const listControls = useStudyListControls(studies);

  const {
    hydrateSeriesSlices,
    hydratedSeries,
    seriesHydrationProgress,
  } = useSeriesHydration({
    studies,
    setStudies,
    downloadFile: (key, onDownloadProgress) => api.downloadSharedImagingFile(token, key, onDownloadProgress),
  });

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    api.getSharedImaging(token)
      .then((response) => {
        if (cancelled) return;
        setShare(response);
        setStudies(sortStudiesByDateDesc((response?.studies || []).map(withSeriesLabels)));
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        setStatus(code === 410 ? 'expired' : code === 404 ? 'not-found' : 'error');
      });
    return () => { cancelled = true; };
  }, [token]);

  const handleSeriesClick = async (studyKey, seriesKey, sliceIndex) => {
    try {
      const result = await hydrateSeriesSlices(studyKey, seriesKey);
      if (!result) return;
      setCurrentStudyUID(studyKey);
      setCurrentSeriesKey(seriesKey);
      setCurrentIndex(sliceIndex || 0);
      setIsViewerOpen(true);
    } catch (err) {
      console.log('handleSeriesClick error: ', err);
    }
  };

  const setStudyLoading = (studyKey, loadingValue) => {
    setStudies((prev) => prev.map((s) => (
      s.studyInstanceUID === studyKey ? { ...s, loading: loadingValue } : s
    )));
  };

  const handleDownloadImage = async (image) => {
    const studyKey = image.studyInstanceUID;
    try {
      const downloaded = await downloadStudyArchive({
        study: image,
        downloadFile: (hash) => api.downloadSharedImagingFile(token, hash),
        onProgress: (progress) => setStudyLoading(studyKey, { type: 'progress', progress }),
      });
      if (!downloaded) {
        toast?.('No files to download.', 'warning');
        return;
      }
      toast?.('Download complete.');
    } catch (err) {
      console.log('handleDownloadImage error: ', err);
      toast?.('Failed to download study.', 'warning');
    } finally {
      setStudyLoading(studyKey, null);
    }
  };

  const expiry = share ? describeShareExpiry(share.expiresAt) : null;
  const studyCount = studies.length;

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <ImagingLoadingState
          title="Loading shared images"
          description="Hang tight while we fetch the shared imaging studies."
        />
      );
    }
    if (status === 'expired') {
      return (
        <ImagingEmptyState
          title="This link has expired"
          description="The person who shared these images set the link to expire. Ask them for a new link if you still need access."
          actionLabel="Go Home"
          onAction={() => navigate('/')}
          Icon={Clock}
          ActionIcon={Home}
        />
      );
    }
    if (status === 'not-found') {
      return (
        <ImagingEmptyState
          title="Share not found"
          description="This link is invalid or the share has been deleted."
          actionLabel="Go Home"
          onAction={() => navigate('/')}
          Icon={Link2Off}
          ActionIcon={Home}
        />
      );
    }
    if (status === 'error') {
      return (
        <ImagingEmptyState
          title="Something went wrong"
          description="We couldn't load this share right now. Please try again in a moment."
          actionLabel="Retry"
          onAction={() => window.location.reload()}
          Icon={Link2Off}
          ActionIcon={Share2}
        />
      );
    }
    if (studyCount === 0) {
      return (
        <ImagingEmptyState
          title="Nothing to show"
          description="This share doesn't contain any studies."
          actionLabel="Go Home"
          onAction={() => navigate('/')}
          Icon={Share2}
          ActionIcon={Home}
        />
      );
    }
    return (
      <>
        <StudyListControls
          search={listControls.search}
          onSearchChange={listControls.setSearch}
          tagOptions={listControls.tagOptions}
          selectedTagKeys={listControls.selectedTagKeys}
          onToggleTag={listControls.toggleTag}
          onClearFilters={listControls.clearFilters}
          hasActiveFilters={listControls.hasActiveFilters}
          visibleCount={listControls.visibleStudies.length}
          totalCount={studyCount}
        />
        {listControls.visibleStudies.length === 0 ? (
          <StudyListNoResults onClearFilters={listControls.clearFilters} />
        ) : (
          <ImageCardList
            studies={listControls.visibleStudies}
            onSeriesClick={handleSeriesClick}
            onDownload={handleDownloadImage}
            onSeriesHydrationPrefetch={(studyUid, seriesUid) => {
              void hydrateSeriesSlices(studyUid, seriesUid);
            }}
            hydratedSeriesLookup={hydratedSeries}
            seriesHydrationProgressBySeriesUid={seriesHydrationProgress}
          />
        )}
      </>
    );
  };

  return (
    <div className="review-list-container" style={{ paddingTop: '0px' }}>
      <header className="imaging-header shared-images-header">
        <div className="imaging-header-top">
          <div className="imaging-header-title-group">
            <Share2 size={20} strokeWidth={2} color="#2980b9" />
            <h1 className="imaging-header-title">Shared Imaging</h1>
          </div>
          {share && (
            <div className="shared-images-meta">
              {share.owner && <span>Shared by <strong>{share.owner}</strong></span>}
              <span>{studyCount} {studyCount === 1 ? 'study' : 'studies'}</span>
              {expiry && expiry.status !== 'never' && (
                <span className="shared-images-expiry">
                  <Clock size={14} strokeWidth={2} />
                  {expiry.label}
                </span>
              )}
            </div>
          )}
        </div>
      </header>

      {renderBody()}

      <DicomViewer
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        studies={studies}
        currentStudyUID={currentStudyUID}
        currentSeriesKey={currentSeriesKey}
        currentIndex={currentIndex}
        onIndexChange={setCurrentIndex}
      />
    </div>
  );
}

export default SharedImagesPage;
